import React, { useEffect, useState, useMemo } from 'react';
import axios from 'axios';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import {
  Loader2, FileText, Download, AlertTriangle, CheckCircle, Clock,
  Send, Mail, Plus, X, ArrowLeft, Users
} from 'lucide-react';
import { useToast } from '../../hooks/use-toast';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const formatExact = (v) => {
  if (v === null || v === undefined || isNaN(v)) return '0';
  return Math.round(v).toLocaleString('fr-FR');
};

const StatTile = ({ label, value, sub, color = 'gray', icon: Icon }) => {
  const palette = {
    emerald: 'border-emerald-200 bg-emerald-50/50 text-emerald-700',
    green: 'border-green-200 bg-green-50/50 text-green-700',
    highlight: 'border-emerald-500 border-l-4 bg-white text-gray-900 shadow-sm',
    gray: 'border-gray-200 bg-gray-50/50 text-gray-700',
  };
  return (
    <div className={`rounded-lg border px-3 py-2.5 ${palette[color]}`}>
      <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider font-semibold">
        {Icon && <Icon className="w-3 h-3" />}
        {label}
      </div>
      <div className="text-base font-bold mt-1 font-mono leading-tight break-words" title={value}>
        {value}
      </div>
      {sub && <div className="text-[10px] text-gray-500 mt-0.5">{sub}</div>}
    </div>
  );
};

const ClientStatementDialog = ({ open, onOpenChange, client }) => {
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [sending, setSending] = useState(false);
  const [preview, setPreview] = useState(null);
  const [error, setError] = useState(null);

  // Email composer state
  const [mode, setMode] = useState('preview'); // 'preview' | 'compose'
  const [selectedEmails, setSelectedEmails] = useState({}); // {email: true/false}
  const [customEmails, setCustomEmails] = useState([]); // [string]
  const [customInput, setCustomInput] = useState('');
  const [customError, setCustomError] = useState('');
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');

  const token = useMemo(() => 'cookie-auth', []);

  useEffect(() => {
    if (!open || !client?.id) {
      setPreview(null);
      setError(null);
      setMode('preview');
      setSelectedEmails({});
      setCustomEmails([]);
      setCustomInput('');
      setSubject('');
      setMessage('');
      return;
    }
    const fetchPreview = async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await axios.get(`${API}/admin/clients/${client.id}/statement/preview`, {
        });
        setPreview(res.data);
        // pre-select all known contacts
        const sel = {};
        (res.data?.contacts || []).forEach(c => { if (c.email) sel[c.email] = true; });
        setSelectedEmails(sel);
        setSubject(`Relevé de compte – ${res.data?.statementNumber || ''} – BAOBAB CONNECT`);
      } catch (e) {
        setError(e?.response?.data?.detail || 'Impossible de charger les données du relevé');
      } finally {
        setLoading(false);
      }
    };
    fetchPreview();
  }, [open, client?.id, token]);

  const handleDownload = async () => {
    if (!client?.id) return;
    setDownloading(true);
    try {
      const res = await axios.post(
        `${API}/admin/clients/${client.id}/statement`,
        {},
        { responseType: 'blob'}
      );
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${preview?.statementNumber || 'releve'}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast({ title: 'Relevé généré', description: 'Le PDF a été téléchargé.' });
    } catch (e) {
      toast({
        title: 'Erreur',
        description: e?.response?.data?.detail || 'Impossible de générer le relevé',
        variant: 'destructive',
      });
    } finally {
      setDownloading(false);
    }
  };

  const recipients = useMemo(() => {
    const fromContacts = Object.entries(selectedEmails).filter(([, on]) => on).map(([e]) => e);
    return Array.from(new Set([...fromContacts, ...customEmails]));
  }, [selectedEmails, customEmails]);

  const handleAddCustom = () => {
    const email = customInput.trim().toLowerCase();
    if (!email) return;
    if (!EMAIL_RE.test(email)) {
      setCustomError('Format d\'email invalide');
      return;
    }
    if (customEmails.includes(email) || selectedEmails[email]) {
      setCustomError('Email déjà ajouté');
      return;
    }
    setCustomEmails(prev => [...prev, email]);
    setCustomInput('');
    setCustomError('');
  };

  const handleRemoveCustom = (email) => {
    setCustomEmails(prev => prev.filter(e => e !== email));
  };

  const toggleContact = (email) => {
    setSelectedEmails(prev => ({ ...prev, [email]: !prev[email] }));
  };

  const handleSend = async () => {
    if (!client?.id || recipients.length === 0) return;
    setSending(true);
    try {
      const res = await axios.post(
        `${API}/admin/clients/${client.id}/statement/email`,
        {
          recipients,
          subject: subject?.trim() || undefined,
          message: message?.trim() || undefined,
        },
        {}
      );
      const { sent = [], failed = [] } = res.data || {};
      if (sent.length && !failed.length) {
        toast({
          title: 'Email envoyé',
          description: `Relevé envoyé à ${sent.length} destinataire(s).`,
        });
        onOpenChange(false);
      } else if (sent.length) {
        toast({
          title: 'Envoi partiel',
          description: `${sent.length} envoyé(s), ${failed.length} échec(s) : ${failed.map(f => f.email).join(', ')}`,
          variant: 'destructive',
        });
      } else {
        toast({
          title: 'Échec d\'envoi',
          description: failed[0]?.error || 'Aucun email n\'a pu être envoyé. Vérifiez la configuration SMTP/SendGrid.',
          variant: 'destructive',
        });
      }
    } catch (e) {
      toast({
        title: 'Erreur',
        description: e?.response?.data?.detail || 'Erreur lors de l\'envoi',
        variant: 'destructive',
      });
    } finally {
      setSending(false);
    }
  };

  const summary = preview?.summary;
  const aging = preview?.aging || [];
  const contacts = preview?.contacts || [];
  const displayName = preview?.client?.displayName || client?.companyName ||
    `${client?.firstName || ''} ${client?.lastName || ''}`.trim() || 'Client';

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="statement-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {mode === 'compose' ? (
              <>
                <Mail className="w-5 h-5 text-emerald-600" />
                Envoyer le relevé par email
              </>
            ) : (
              <>
                <FileText className="w-5 h-5 text-emerald-600" />
                Relevé de compte — {displayName}
              </>
            )}
          </DialogTitle>
          <DialogDescription className="text-xs">
            {mode === 'compose'
              ? `Le PDF du relevé sera joint automatiquement. Sélectionnez les destinataires.`
              : `Synthèse comptable en temps réel. Le PDF contient le détail complet.`}
          </DialogDescription>
        </DialogHeader>

        {loading && (
          <div className="flex items-center justify-center py-12 text-gray-500" data-testid="statement-loading">
            <Loader2 className="w-6 h-6 animate-spin mr-2" />
            Chargement des données financières…
          </div>
        )}

        {error && !loading && (
          <div className="bg-red-50 border border-red-200 rounded p-4 text-sm text-red-700">
            <AlertTriangle className="w-4 h-4 inline mr-1" /> {error}
          </div>
        )}

        {/* ============ PREVIEW MODE ============ */}
        {summary && !loading && mode === 'preview' && (
          <div className="space-y-4" data-testid="statement-preview">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2.5">
              <StatTile label="Total facturé" value={formatExact(summary.totalInvoiced)} sub={`${summary.invoiceCount} facture(s) — FCFA`} color="emerald" icon={FileText} />
              <StatTile label="Total réglé" value={formatExact(summary.totalPaid)} sub={`${summary.paidCount} payée(s) — FCFA`} color="green" icon={CheckCircle} />
              <StatTile label="Solde à régler" value={formatExact(summary.totalDue)} sub={`${summary.unpaidCount} en attente — FCFA`} color="highlight" icon={AlertTriangle} />
              <StatTile label="DSO moyen" value={`${summary.dso} j`} sub={summary.overdueCount > 0 ? `${summary.overdueCount} en retard` : 'Aucun retard'} color="gray" icon={Clock} />
            </div>

            {summary.unpaidCount > 0 && aging.length > 0 && (
              <div>
                <p className="text-xs font-semibold text-gray-700 uppercase tracking-wider mb-2">
                  Vieillissement des créances impayées
                </p>
                <div className="grid grid-cols-4 gap-2">
                  {aging.map((b, i) => {
                    const tones = [
                      'bg-emerald-50 border-emerald-200 text-emerald-700',
                      'bg-gray-50 border-gray-200 text-gray-700',
                      'bg-gray-100 border-gray-300 text-gray-800',
                      'bg-gray-200 border-gray-400 text-gray-900',
                    ];
                    const tone = tones[i] || tones[0];
                    return (
                      <div key={b.label} className={`rounded border px-2 py-1.5 text-center ${tone}`}>
                        <div className="text-[10px] font-semibold">{b.label}</div>
                        <div className="text-sm font-bold font-mono mt-0.5">{formatExact(b.amount)}</div>
                        <div className="text-[9px] text-gray-500">{b.count} fact.</div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {summary.unpaidCount === 0 && (
              <div className="bg-emerald-50 border border-emerald-200 rounded p-3 text-sm text-emerald-700 flex items-center gap-2">
                <CheckCircle className="w-4 h-4" />
                <span>Aucune facture impayée — le compte est à jour.</span>
              </div>
            )}

            <div className="text-[11px] text-gray-500 italic">
              Document n° <span className="font-mono">{preview?.statementNumber}</span> — édité le {preview?.issueDate}.
            </div>
          </div>
        )}

        {/* ============ COMPOSE MODE ============ */}
        {summary && !loading && mode === 'compose' && (
          <div className="space-y-4" data-testid="statement-compose">
            {/* Contacts */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <p className="text-xs font-semibold text-gray-700 uppercase tracking-wider flex items-center gap-1.5">
                  <Users className="w-3.5 h-3.5" />
                  Contacts du client {contacts.length > 0 && `(${contacts.length})`}
                </p>
              </div>
              {contacts.length === 0 ? (
                <p className="text-xs text-gray-400 italic">Aucun contact email enregistré pour ce client.</p>
              ) : (
                <div className="space-y-1.5">
                  {contacts.map((c) => (
                    <label key={c.email} className="flex items-center gap-2 p-2 rounded border border-gray-200 hover:border-emerald-300 cursor-pointer text-sm" data-testid={`contact-row-${c.email}`}>
                      <input
                        type="checkbox"
                        checked={!!selectedEmails[c.email]}
                        onChange={() => toggleContact(c.email)}
                        className="w-4 h-4 accent-emerald-600 cursor-pointer"
                      />
                      <div className="flex-1 min-w-0">
                        <div className="font-medium text-gray-900 truncate">{c.name}</div>
                        <div className="text-[11px] text-gray-500 truncate">{c.email}</div>
                      </div>
                    </label>
                  ))}
                </div>
              )}
            </div>

            {/* Custom emails */}
            <div>
              <p className="text-xs font-semibold text-gray-700 uppercase tracking-wider mb-2">
                Ajouter un email
              </p>
              <div className="flex gap-2">
                <Input
                  type="email"
                  value={customInput}
                  onChange={(e) => { setCustomInput(e.target.value); setCustomError(''); }}
                  onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleAddCustom(); } }}
                  placeholder="autre@exemple.com"
                  className="h-9 text-sm"
                  data-testid="custom-email-input"
                />
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  onClick={handleAddCustom}
                  className="h-9 shrink-0"
                  data-testid="custom-email-add"
                >
                  <Plus className="w-3.5 h-3.5 mr-1" />
                  Ajouter
                </Button>
              </div>
              {customError && <p className="text-[11px] text-red-600 mt-1">{customError}</p>}
              {customEmails.length > 0 && (
                <div className="flex flex-wrap gap-1.5 mt-2">
                  {customEmails.map((email) => (
                    <span key={email} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-50 border border-emerald-200 text-emerald-800 text-xs" data-testid={`custom-chip-${email}`}>
                      {email}
                      <button onClick={() => handleRemoveCustom(email)} className="hover:bg-emerald-200 rounded-full p-0.5">
                        <X className="w-2.5 h-2.5" />
                      </button>
                    </span>
                  ))}
                </div>
              )}
            </div>

            {/* Subject */}
            <div>
              <p className="text-xs font-semibold text-gray-700 uppercase tracking-wider mb-1.5">Sujet</p>
              <Input
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                placeholder="Relevé de compte – BAOBAB CONNECT"
                className="h-9 text-sm"
                data-testid="email-subject-input"
              />
            </div>

            {/* Message */}
            <div>
              <p className="text-xs font-semibold text-gray-700 uppercase tracking-wider mb-1.5">
                Message personnalisé <span className="text-gray-400 normal-case">(optionnel)</span>
              </p>
              <Textarea
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                placeholder="Bonjour, vous trouverez ci-joint votre relevé de compte…"
                rows={3}
                className="text-sm"
                data-testid="email-message-input"
              />
              <p className="text-[10px] text-gray-400 mt-1 italic">
                Si laissé vide, un message par défaut sera utilisé (montant dû + références).
              </p>
            </div>

            {/* Recap */}
            <div className="bg-emerald-50 border border-emerald-200 rounded p-3 text-xs">
              <div className="flex items-center gap-2 mb-1 text-emerald-800 font-semibold">
                <Send className="w-3.5 h-3.5" />
                Récapitulatif d'envoi
              </div>
              <div className="text-gray-700">
                <strong>{recipients.length}</strong> destinataire(s) · PDF joint : <span className="font-mono">{preview?.statementNumber}.pdf</span>
              </div>
            </div>
          </div>
        )}

        <DialogFooter className="flex-col sm:flex-row gap-2">
          {mode === 'preview' ? (
            <>
              <Button
                variant="outline"
                onClick={() => onOpenChange(false)}
                data-testid="statement-cancel-btn"
              >
                Fermer
              </Button>
              <Button
                variant="outline"
                onClick={() => setMode('compose')}
                disabled={loading || !!error}
                className="border-emerald-600 text-emerald-700 hover:bg-emerald-50"
                data-testid="statement-email-mode-btn"
              >
                <Mail className="w-4 h-4 mr-2" />
                Envoyer par email
              </Button>
              <Button
                onClick={handleDownload}
                disabled={loading || !!error || downloading}
                className="bg-emerald-600 hover:bg-emerald-700 text-white"
                data-testid="statement-download-btn"
              >
                {downloading ? (
                  <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Génération…</>
                ) : (
                  <><Download className="w-4 h-4 mr-2" /> Télécharger le PDF</>
                )}
              </Button>
            </>
          ) : (
            <>
              <Button
                variant="outline"
                onClick={() => setMode('preview')}
                disabled={sending}
                data-testid="statement-back-btn"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Retour
              </Button>
              <Button
                onClick={handleSend}
                disabled={recipients.length === 0 || sending}
                className="bg-emerald-600 hover:bg-emerald-700 text-white"
                data-testid="statement-send-email-btn"
              >
                {sending ? (
                  <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Envoi en cours…</>
                ) : (
                  <><Send className="w-4 h-4 mr-2" /> Envoyer ({recipients.length})</>
                )}
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ClientStatementDialog;
